import { debounce } from '../../../utils/debounce'
import { superScriptMapping } from '../../../utils/superScriptMapping'
import { sanitizeInput } from '../../../utils/sanitize'
import { KeyboardEvent } from 'react'

/**
 * Toast Singleton - Picks up click events on the page and opens associated toast
 */

/**
 * Every `data-legal-*` attribute the disclosure builder consumes. Matching these natively is what
 * keeps disclosure setup off the "walk every element and inspect its dataset" path, so any new
 * `data-legal-*` attribute has to be added here to be picked up.
 */
const LEGAL_SELECTOR = '[data-legal-msg],[data-legal-uri],[data-legal-indicator],[data-legal-btnstyle]'

/**
 * The only containers that collect their disclosures into a numbered list. Every `gmx-panel` renders a
 * `.panel-container` whatever its `data-panel-type`, so the panel side has to stay scoped to the compare
 * panel: a disclosure treated as container-scoped without a list to join never becomes a toast trigger.
 */
const COMPARE_PANEL_SELECTOR = 'gmx-panel[data-panel-type="inset-expanded-compare"]'
const DISCLOSURE_LIST_CONTAINER_SELECTOR = `.overlay-container, ${COMPARE_PANEL_SELECTOR} .panel-container`

/**
 * Components that opt out of numbering. These tiles hold their own `.overlay-container` for expanded
 * content, so their disclosures would otherwise be collected into a list at the bottom of the tile
 * overlay; they are meant to read as inline toast triggers next to the colour or wheel they describe.
 */
const DISCLOSURE_NUMBERING_EXEMPT_SELECTOR = 'gmx-colorizer-tile, gmx-wheel-tile'

const isToastActiveMap = new WeakMap<GMXToastSingleton, boolean>()
const activeToastElementMap = new WeakMap<GMXToastSingleton, HTMLElement | null>()
const toastTriggerElementMap = new WeakMap<GMXToastSingleton, HTMLElement | null>()

class GMXToastSingleton {
	toastHeaderButtonTemplate = `<button class="gmx-btn gmx-btn-link-primary icon-only gmx-invert" aria-label="close toast" data-icon-name="close"></button>`
	mutationObserver: MutationObserver
	private pendingMutationNodes = new Set<HTMLElement>()

	/**
	 * Cache of disclosure requests keyed by URI. Pages routinely repeat the same
	 * `data-toast-uri`/`data-legal-uri` across many elements, so the promise (not just the
	 * resolved value) is stored: concurrent lookups for a URI share one in-flight request,
	 * and later lookups resolve straight from the cache without hitting the network.
	 */
	private disclosureRequestCache = new Map<string, Promise<string>>()

	/** Per-list lookup of disclosure markup to its position, used to dedupe repeated disclosures. */
	private disclosureIndexCache = new WeakMap<HTMLOListElement, { childCount: number; indices: Map<string, number> }>()

	/** Roots already wired for toast triggers, so re-injected content cannot stack duplicate listeners. */
	private wiredToastRoots = new WeakSet<ShadowRoot | HTMLElement>()

	/** Pending auto-dismiss timer per toast, so whichever path closes a toast can cancel its teardown. */
	private autoDismissTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>()

	/**
	 * Debounced flush of nodes queued by the MutationObserver. Progressive one-at-a-time
	 * DOM inserts coalesce into a single legal-disclosure setup pass after inserts settle.
	 */
	private flushPendingMutations = debounce(() => {
		const nodes = Array.from(this.pendingMutationNodes)
		this.pendingMutationNodes.clear()

		// Walking up from each node against the queue finds covered nodes in one pass per node, rather
		// than testing `contains` against every other queued node.
		const queued = new Set(nodes)
		for (const node of nodes) {
			if (!node.isConnected) {
				continue
			}

			let isCovered = false
			for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
				if (queued.has(ancestor)) {
					isCovered = true
					break
				}
			}

			if (!isCovered) {
				this._setupLegalBuilderHelper(node)
			}
		}
	}, 100)

	get activeToastElement(): HTMLElement | null {
		return activeToastElementMap.get(this) || null
	}
	set activeToastElement(val: HTMLElement | null) {
		activeToastElementMap.set(this, val)
	}

	get toastTriggerElement(): HTMLElement | null {
		return toastTriggerElementMap.get(this) || null
	}
	set toastTriggerElement(val: HTMLElement | null) {
		toastTriggerElementMap.set(this, val)
	}

	get isToastActive(): boolean {
		return isToastActiveMap.get(this) || false
	}

	set isToastActive(val: boolean) {
		isToastActiveMap.set(this, val)
	}

	constructor() {
		isToastActiveMap.set(this, false)
		// @ts-expect-error:  adding global class to window
		if (!window.gmxToastSingleton) {
			// @ts-expect-error:  adding global class to window
			window.gmxToastSingleton = this

			// Setup a mutation observer to look for new elements added to the DOM that may have disclosures
			this.mutationObserver = new MutationObserver(this._onDOMMutation.bind(this))

			this.mutationObserver.observe(document.getElementsByTagName('body')[0], {
				childList: true,
				subtree: true,
			})

			//initialize any existing disclosures on the page
			this._setupLegalBuilderHelper(document.body)

			this.setupToastShowListener()
		} else {
			window.console.log('Tried to instantiate toast singleton...already instantiated')
		}
	}

	/**
	 * Callback for the MutationObserver setup on the body to look for newly added elements and if they have disclosures as children
	 * that may need to be initialized. Queues nodes and debounces processing so progressive
	 * DOM builds do not run legal setup on every individual insert.
	 *
	 * Only nodes that are, or contain, a disclosure are queued. Building a disclosure inserts elements
	 * of its own, so queueing indiscriminately would feed this observer its own output and schedule
	 * another pass over the subtree for every disclosure built.
	 *
	 * @param mutations {MutationRecord[]}
	 * @private
	 */
	private _onDOMMutation(mutations: MutationRecord[]): void {
		let hasPendingNodes = false

		for (const mut of mutations) {
			if (mut.target.nodeType !== 1) {
				continue
			}

			for (const node of mut.addedNodes) {
				if (node.nodeType !== 1) {
					continue
				}

				const el = node as HTMLElement
				if (el.matches(LEGAL_SELECTOR) || el.querySelector(LEGAL_SELECTOR)) {
					this.pendingMutationNodes.add(el)
					hasPendingNodes = true
				}
			}
		}

		if (hasPendingNodes) {
			this.flushPendingMutations()
		}
	}

	private _replaceWithSuperscript(text: string): string {
		// Only digits have a superscript entity, so anything else (an asterisk, a letter) passes through
		return text.replace(/\d/g, char => superScriptMapping[char])
	}

	private _removeLegalHelper(el: HTMLElement): void {
		// Remove the data-legal-msg and data-legal-uri attributes to prevent double processing
		if (el.dataset.legalMsg) {
			delete el.dataset.legalMsg
		}
		if (el.dataset.legalUri) {
			delete el.dataset.legalUri
		}
		if (el.dataset.legalIndicator) {
			delete el.dataset.legalIndicator
		}
		if (el.dataset.legalBtnstyle) {
			delete el.dataset.legalBtnstyle
		}
	}

	private _checkForOCDisclosureContainer(overlayContainer: HTMLElement): HTMLElement {
		let OCDisclosureContainer = overlayContainer.querySelector('.overlay-disclosures-container') as HTMLElement
		if (!OCDisclosureContainer) {
			// If the overlay container doesn't have a legal list, then we need to create one
			OCDisclosureContainer = document.createElement('div')
			OCDisclosureContainer.classList.add('overlay-disclosures-container')
			overlayContainer.querySelector('.overlay-content-body').appendChild(OCDisclosureContainer)
		}
		return OCDisclosureContainer
	}

	private _checkForPanelDisclosureContainer(panelContainer: HTMLElement): HTMLElement {
		let panelDisclosureContainer = panelContainer.querySelector('.panel-disclosures-container') as HTMLElement
		if (!panelDisclosureContainer) {
			// If the panel container doesn't have a legal list, then we need to create one
			panelDisclosureContainer = document.createElement('div')
			panelDisclosureContainer.classList.add('panel-disclosures-container')
			const panelContent = panelContainer.querySelector('.panel-content') as HTMLElement | null
			const disclosureParent = panelContent || panelContainer
			disclosureParent.appendChild(panelDisclosureContainer)
		}
		return panelDisclosureContainer
	}

	private _checkForDisclosureList(OCDisclosureContainer: HTMLElement): HTMLOListElement {
		let OCList: HTMLOListElement = OCDisclosureContainer.querySelector('ol.disclosure-list') as HTMLOListElement
		if (!OCList) {
			OCList = document.createElement('ol')
			OCList.classList.add('disclosure-list')
			OCDisclosureContainer.appendChild(OCList)
		}
		return OCList
	}

	private _addSuperScriptSup(el: HTMLElement, legalIndicator: string): void {
		const legalSup: HTMLElement = document.createElement('sup')
		// If the element has a data-legal-indicator attribute,
		// then we should try to convert it to superscript
		if (legalIndicator) {
			legalSup.innerHTML = this._replaceWithSuperscript(legalIndicator)
		} else {
			// If the element:
			// has no data-legal-indicator attribute,
			// then we just use a single asterisk
			legalSup.textContent = '*'
		}

		// Insert the legal button into the element at the end
		el.insertAdjacentElement('beforeend', legalSup)
	}

	/**
	 * Position of each distinct disclosure already in a list, so repeated disclosures can be matched by
	 * lookup. Comparing against every existing row instead means re-serializing the whole list for each
	 * insert, which is quadratic in the number of disclosures.
	 */
	private _getDisclosureIndex(list: HTMLOListElement): Map<string, number> {
		const cached = this.disclosureIndexCache.get(list)
		if (cached && cached.childCount === list.children.length) {
			return cached.indices
		}

		// A list can also be populated outside of this pass, so rebuild whenever the row count drifts
		const indices = new Map<string, number>()
		for (let i = 0; i < list.children.length; i++) {
			const html = list.children[i].innerHTML
			if (!indices.has(html)) {
				indices.set(html, i + 1)
			}
		}
		this.disclosureIndexCache.set(list, { childCount: list.children.length, indices })

		return indices
	}

	private async _addDisclosureItem(OCList: HTMLOListElement, el: HTMLElement): Promise<string> {
		const disclosureHtml = (await this._getToastMsg(el, 'legal')) || ''
		const indices = this._getDisclosureIndex(OCList)

		// does this item already exist? If so, we're not going to add it.
		const existingIndicator = indices.get(disclosureHtml)
		if (existingIndicator != null) {
			return existingIndicator.toString()
		}

		const listEl: HTMLLIElement = document.createElement('li')
		listEl.innerHTML = disclosureHtml
		OCList.appendChild(listEl)

		const indicator = OCList.children.length
		indices.set(disclosureHtml, indicator)
		this.disclosureIndexCache.set(OCList, { childCount: indicator, indices })

		return indicator.toString()
	}

	private _addSuperScript(el: HTMLElement) {
		const legalBtn: HTMLElement = document.createElement('span') as HTMLSpanElement
		legalBtn.role = 'button'
		legalBtn.tabIndex = 0
		legalBtn.setAttribute('data-toast-dismiss', 'manual')
		legalBtn.setAttribute('data-toast-style', 'legal')

		if (el.dataset.legalBtnstyle) {
			legalBtn.classList.add('gmx-btn', `gmx-btn-${el.dataset.legalBtnstyle}`)
		}

		if (el.dataset.legalUri) {
			legalBtn.setAttribute('data-toast-uri', el.dataset.legalUri)
		}

		if (el.dataset.legalMsg) {
			legalBtn.setAttribute('data-toast-msg', el.dataset.legalMsg)
		}

		// Move the existing children rather than copying markup, which avoids serializing and
		// reparsing the element's content on the way in and again on the way out
		legalBtn.append(...el.childNodes)

		this._addSuperScriptSup(legalBtn, el.dataset.legalIndicator)

		// If the original element was a <span>, since we're creating a new span for the disclosure, just replace, else replace inner markup of element with new span
		if (el.tagName.toLowerCase() === 'span') {
			el.replaceWith(legalBtn)
		} else {
			el.replaceChildren(legalBtn)
		}
	}

	/**
	 * Whether a disclosure belongs to a container that numbers and lists its disclosures at the bottom,
	 * rather than becoming a standalone toast trigger where it sits.
	 *
	 * @private
	 */
	private _isContainerScopedDisclosure(el: HTMLElement): boolean {
		return !!el.closest(DISCLOSURE_LIST_CONTAINER_SELECTOR) && !el.closest(DISCLOSURE_NUMBERING_EXEMPT_SELECTOR)
	}

	/**
	 * Method to help build disclosures with minimal effort
	 * @private
	 */
	private _setupLegalBuilderHelper(el: HTMLElement | ShadowRoot): void {
		// One native query finds every disclosure in the subtree, instead of walking all of its elements
		// and inspecting each dataset in JS.
		const legalElements = Array.from(el.querySelectorAll(LEGAL_SELECTOR)) as HTMLElement[]

		// Disclosures found in an Overlay or Compare Table Panel should be numbered and listed at the bottom
		// of the Overlay or Panel container; everything else becomes a standalone toast trigger. Splitting by
		// `closest` keeps disclosures matched when the container is an ancestor of `el` itself, which is how
		// the MutationObserver and the injector call this with individual inserted nodes.
		const containerLegalElements: HTMLElement[] = []
		const defaultLegalElements: HTMLElement[] = []
		for (const element of legalElements) {
			if (this._isContainerScopedDisclosure(element)) {
				containerLegalElements.push(element)
			} else {
				defaultLegalElements.push(element)
			}
		}

		// If the element has any legal attributes, push onto the array; classified the same way as its
		// descendants so a disclosure handed in directly is not treated differently. A ShadowRoot
		// (passed by the injector via setupDisclosures) has no dataset and cannot
		// itself be a legal element, so only check when `el` is an element.
		if (el instanceof HTMLElement && (el.dataset.legalMsg || el.dataset.legalUri)) {
			if (this._isContainerScopedDisclosure(el)) {
				containerLegalElements.push(el)
			} else {
				defaultLegalElements.push(el)
			}
		}

		void this._buildDisclosures(containerLegalElements, defaultLegalElements)
	}

	/**
	 * Number the container-scoped disclosures, then turn every disclosure into a superscript toast trigger.
	 *
	 * Container messages are prefetched together so that a container holding N disclosures costs one round
	 * of parallel requests rather than N serialized ones. The numbering loop that follows stays sequential
	 * to keep list order matching document order, but it now resolves from the cache instead of the network.
	 *
	 * @private
	 */
	private async _buildDisclosures(
		containerLegalElements: HTMLElement[],
		defaultLegalElements: HTMLElement[]
	): Promise<void> {
		await Promise.all(containerLegalElements.map(element => this._getToastMsg(element, 'legal')))

		// Process container legal elements sequentially to guarantee order
		for (const element of containerLegalElements) {
			// Check for the legal list
			// First find the overlay container
			const overlayContainer = element.closest('.overlay-container') as HTMLElement
			if (overlayContainer) {
				const OCDisclosureContainer = this._checkForOCDisclosureContainer(overlayContainer)
				if (OCDisclosureContainer) {
					// Check for List and create it if it doesn't exist
					const OCList: HTMLOListElement = this._checkForDisclosureList(OCDisclosureContainer)
					if (OCList) {
						// Add the disclosure row to the list
						const indicator = await this._addDisclosureItem(OCList, element)

						// If the element has a data-legal-indicator attribute,
						// then we should use that as the indicator for the superscript,
						// otherwise we use the number in the list as the indicator
						if (!element.dataset.legalIndicator) {
							element.setAttribute('data-legal-indicator', indicator)
						}
					}
				}
			} else {
				// Not in an overlay, so look for the panel.  This logic should ONLY apply to disclosures in a compare table panel
				const comparePanelEl = element.closest(COMPARE_PANEL_SELECTOR) as HTMLElement
				const panelContainer = comparePanelEl?.querySelector('.panel-container') as HTMLElement
				if (panelContainer) {
					const panelDisclosureContainer = this._checkForPanelDisclosureContainer(panelContainer)
					if (panelDisclosureContainer) {
						const panelList = this._checkForDisclosureList(panelDisclosureContainer)
						if (panelList) {
							// Add the disclosure row to the list
							const indicator = await this._addDisclosureItem(panelList, element)

							// If the element has a data-legal-indicator attribute,
							// then we should use that as the indicator for the superscript,
							// otherwise we use the number in the list as the indicator
							if (!element.dataset.legalIndicator) {
								element.setAttribute('data-legal-indicator', indicator)
							}
						}
					}
				}
			}
		}

		// After we process all the legal elements in the overlay container,
		// we need to loop through them again to add the superscript buttons,
		// since we needed to wait until we processed all of them to know the correct indicator for each element
		for (const element of containerLegalElements) {
			// Add the super script; If the element has a data-legal-indicator attribute,
			this._addSuperScriptSup(element, element.dataset.legalIndicator)

			// Remove the legal helper stuff
			this._removeLegalHelper(element)
		}

		// Process default legal elements sequentially to guarantee order
		for (const element of defaultLegalElements) {
			// Build the legal button
			this._addSuperScript(element)

			// Remove the legal helper stuff
			this._removeLegalHelper(element)
		}
	}

	/**
	 * Build any `data-legal-*` disclosures found within `root`. The constructor only
	 * processes `document.body` (plus its body-scoped MutationObserver), neither of
	 * which can reach content encapsulated in a shadow root. `GMXInjector` calls this
	 * with its `ShadowRoot` after injecting experience-fragment content so disclosures
	 * authored inside the shadow DOM are still converted to superscripts/disclosure
	 * lists, mirroring how the other singletons accept the injector's root.
	 */
	public setupDisclosures(root: ShadowRoot | HTMLElement = document.body): void {
		this._setupLegalBuilderHelper(root)
	}

	private _lookForToast(el: HTMLElement): HTMLElement {
		// `closest` tests the element itself first, then looks up through ancestors in case a child
		// element captured the click event
		return el.closest('[data-toast-msg],[data-toast-uri]') as HTMLElement
	}

	/**
	 * Fetch and sanitize the disclosure content for a URI, reusing the cached request when one
	 * exists. Failed requests are evicted so a later click or DOM insert can retry them.
	 *
	 * @param uri {string}
	 * @private
	 */
	private _fetchDisclosure(uri: string): Promise<string> {
		const cachedRequest = this.disclosureRequestCache.get(uri)
		if (cachedRequest) {
			return cachedRequest
		}

		const request = fetch(uri)
			.then(async response => {
				if (!response.ok) {
					throw new Error(`Response status: ${response.status}`)
				}
				const result = await response.json()
				const disclosureDescription = (result as { disclosureDescription?: unknown })?.disclosureDescription
				if (typeof disclosureDescription !== 'string') {
					throw new Error('Invalid disclosure response: missing disclosureDescription')
				}

				return window.gmxDOMPurify
					? window.gmxDOMPurify.sanitize(disclosureDescription, {
							ALLOWED_ATTR: ['href', 'target', 'rel'],
						})
					: sanitizeInput(disclosureDescription)
			})
			.catch(error => {
				// Don't hold on to a failed request, otherwise the error is replayed forever
				this.disclosureRequestCache.delete(uri)
				throw error
			})

		this.disclosureRequestCache.set(uri, request)

		return request
	}

	/**
	 * Drop every cached disclosure request, forcing the next lookup for each URI to re-fetch.
	 * Useful when disclosure content changes behind the same URIs, i.e., after a soft navigation.
	 */
	public clearDisclosureCache(): void {
		this.disclosureRequestCache.clear()
	}

	private async _getToastMsg(el: HTMLElement, type: string): Promise<string> {
		// If the element has a data-toast-msg attribute, then we use that
		if (el.getAttribute(`data-${type}-msg`)) {
			return el.getAttribute(`data-${type}-msg`)
		} else if (el.getAttribute(`data-${type}-uri`)) {
			// If the element has a data-toast-uri attribute, then we need to fetch the content from that URI
			const uri = el.getAttribute(`data-${type}-uri`)
			if (uri) {
				try {
					// Served from the URI cache when this disclosure was already requested
					const sanitizedDisclosureDescription = await this._fetchDisclosure(uri)

					// Cache the result on the element so we don't have to look it up again
					el.setAttribute(`data-${type}-msg`, sanitizedDisclosureDescription)

					return sanitizedDisclosureDescription
				} catch (error) {
					window.console.error(`Error fetching toast message from URI: ${error.message}`)
				}
			}
		}
	}

	/**
	 * Method to setup a click event listener on the body, looking for elements that were clicked that are
	 * to open a toast on the page, ie, has attribute [data-toast-msg].
	 *
	 * A root is only wired once; the injector calls this per injected shadow root, and stacked handlers
	 * would repeat the trigger lookup on every click.
	 */
	public setupToastShowListener(root: ShadowRoot | HTMLElement = document.body): void {
		if (this.wiredToastRoots.has(root)) {
			return
		}
		this.wiredToastRoots.add(root)

		const triggerCallbackFn = async (e: Event | KeyboardEvent) => {
			// If this is a keyboard event, if the user preses enter, we want to pass through and look to see if this is a toast trigger, but for any other key presses, we want to ignore it
			if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Enter') {
				return
			}

			let target = e.target as HTMLElement

			// Skip click events that bubbled up from the shadow root of the injector
			if (target.shadowRoot && target.nodeName === 'GMX-INJECTOR') {
				return
			}

			// Look for a toast
			target = this._lookForToast(target)

			// If we have an active toast element, and the click was outside of it, then we close the toast
			if (this.activeToastElement) {
				if (!this.activeToastElement.contains(e.target as Node)) {
					this._removeToast(this.activeToastElement, this.toastTriggerElement)
				}
			}

			if (target) {
				e.preventDefault()
				if (!this.isToastActive) {
					// If we found a toast, then we need to get the message
					const toastMessage: string = await this._getToastMsg(target, 'toast')

					// If we have a toast message, then we can pop the toast
					if (toastMessage) {
						// disable further clicks on the element until the toast is closed
						target.setAttribute('disabled', '')

						let toastStyle = target.getAttribute('data-toast-style')
						//fallback state
						if (!toastStyle) {
							toastStyle = 'legal'
						}

						let toastDismiss = target.getAttribute('data-toast-dismiss')
						//fallback state
						if (!toastDismiss) {
							toastDismiss = 'manual'
						}

						// Pop the toast
						this.popToast(toastStyle, toastDismiss, toastMessage, root, target)
					}
				}
			}
		}
		root.addEventListener('click', triggerCallbackFn)
		root.addEventListener('keydown', triggerCallbackFn)
	}

	private _removeToast(toastEl: HTMLElement, poppingElement?: HTMLElement): void {
		// Every removal path funnels through here, so cancelling the auto-dismiss timer for this toast
		// covers closing by click-outside, close button, Escape and the shown event's callback alike. A
		// surviving timer would fire later and tear down whichever toast is active at that point.
		const autoDismissTimer = this.autoDismissTimers.get(toastEl)
		if (autoDismissTimer) {
			clearTimeout(autoDismissTimer)
			this.autoDismissTimers.delete(toastEl)
		}

		// Need a delay before we remove the element from the DOM to allow the animation to finish.
		// The fallback covers the cases where no transition ever runs, i.e. reduced motion, which would
		// otherwise strand the toast in the DOM and leave its trigger permanently disabled.
		const finalizeRemoval = () => {
			clearTimeout(transitionFallback)
			toastEl.remove()
			if (poppingElement) {
				poppingElement.removeAttribute('disabled')
			}
		}

		toastEl.addEventListener('transitionend', finalizeRemoval, { once: true })
		// Comfortably longer than the hide transition so it only ever fires when no transition ran
		const transitionFallback = setTimeout(finalizeRemoval, 1000)

		toastEl.classList.remove('gmx-show-toast')

		// Only surrender the singleton state if this is still the toast holding it, so a late removal of
		// an older toast cannot deactivate a newer one
		if (!this.activeToastElement || this.activeToastElement === toastEl) {
			this.isToastActive = false
			this.activeToastElement = null
			this.toastTriggerElement = null
		}

		window.dispatchEvent(new CustomEvent('gmx-toast-removed', { detail: { toastElement: toastEl } }))
	}

	public popToast(
		toastStyle: string,
		toastDismiss: string,
		toastMsg: string,
		root: HTMLElement | ShadowRoot,
		poppingElement?: HTMLElement
	): void {
		this.isToastActive = true

		// Setup the Toast element
		const thisToastEl: HTMLElement = document.createElement('gmx-toast')
		thisToastEl.setAttribute('data-dismiss-type', toastDismiss)
		thisToastEl.classList.add(`gmx-toast-${toastStyle}`)

		// Setup the Toast body element
		const thisToastBodyEl: HTMLElement = document.createElement('div')
		thisToastBodyEl.classList.add('toast-body')

		// Setup the Toast header element
		const thisToastHeaderEl: HTMLElement = document.createElement('div')
		thisToastHeaderEl.classList.add('toast-header')

		// Setup the Toast content element
		const thisToastContentEl: HTMLElement = document.createElement('div')
		thisToastContentEl.classList.add('toast-content')

		// Add the content to the toast
		thisToastContentEl.innerHTML = window.gmxDOMPurify
			? window.gmxDOMPurify.sanitize(toastMsg, {
					ALLOWED_ATTR: ['href', 'target', 'rel'],
				})
			: sanitizeInput(toastMsg)

		const removeToast = () => this._removeToast(thisToastEl, poppingElement)

		// Action on the type of toast
		switch (toastDismiss) {
			case 'auto': {
				// Setup the auto toast to close after 10 seconds. Tracked against the toast so that closing
				// it by any other route cancels the pending teardown; see _removeToast.
				this.autoDismissTimers.set(thisToastEl, setTimeout(removeToast, 10000))
				break
			}
			case 'manual': {
				// If the toast is a manual toast, then we need to add a close button to the header
				thisToastHeaderEl.innerHTML = this.toastHeaderButtonTemplate

				// Setup the close button
				const thisToastHeaderButtonEl: HTMLElement = thisToastHeaderEl.querySelector('button')
				thisToastHeaderButtonEl.addEventListener('click', () => {
					removeToast()
				})

				thisToastEl.addEventListener('keydown', e => {
					if (e.key === 'Escape') {
						removeToast()
					}
				})

				break
			}
			default: {
				this.isToastActive = false

				if (poppingElement) {
					poppingElement.removeAttribute('disabled')
				}
				window.console.warn(`Error:  Toast did not have a type. Should be either 'auto' or 'manual'`)
				break
			}
		}

		// Setup the structure of the toast
		thisToastBodyEl.appendChild(thisToastHeaderEl)
		thisToastBodyEl.appendChild(thisToastContentEl)
		thisToastEl.appendChild(thisToastBodyEl)

		// Append the toast to the body
		const liveToast = root.appendChild(thisToastEl)
		this.activeToastElement = liveToast
		this.toastTriggerElement = poppingElement || null

		// Need a delay before we do the animation for it to appear
		setTimeout(() => {
			liveToast.classList.add('gmx-show-toast')
			window.dispatchEvent(
				new CustomEvent('gmx-toast-shown', { detail: { toastElement: liveToast, toastMsg, removeToast } })
			)
		}, 100)

		// Set focus to the toast close button if it exists
		const closeButtonEl = liveToast.querySelector('button')
		if (closeButtonEl) {
			closeButtonEl.focus()
		}
	}
}

const toastSingleton = Object.freeze(new GMXToastSingleton())
export default toastSingleton
